<template>
    <div class="flex flex-col gap-4 p-6 bg-slate-100">
        <vx-alert
            v-if="!FEATURES.simple_message_hub && agentName && activeThread?.status === 'open'"
            :closeable="false"
            shadow
            class="flex items-center p-4 min-h-16"
        >
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="faPerson"></font-awesome-icon>
            </template>
            Assigned to <strong>{{ agentName }}</strong>
        </vx-alert>
        <conversation-input
            v-if="FEATURES.simple_message_hub || activeThread?.status !== 'closed' || contactNotFound"
        ></conversation-input>
        <div
            v-if="!FEATURES.simple_message_hub"
            class="flex items-center justify-between gap-2"
            data-test="action-buttons"
        >
            <template v-if="activeThread?.status === 'open'">
                <vx-button
                    color="muted"
                    size="lg"
                    block
                    :disabled="actionInProgress"
                    :loading="changingThreadStatus"
                    @click="showCloseWithSnippetsModal = true"
                >
                    Close w/ Snippet <font-awesome-icon widthAuto :icon="faCircleMinus"></font-awesome-icon>
                </vx-button>
                <vx-button
                    color="muted"
                    size="lg"
                    block
                    @click="changeThreadState('closed')"
                    :disabled="actionInProgress"
                    :loading="changingThreadStatus"
                >
                    Close <font-awesome-icon widthAuto :icon="faBan"></font-awesome-icon>
                </vx-button>
                <message-builder-snippets
                    v-model:visible="showCloseWithSnippetsModal"
                    @selected="closeWithSnippet($event)"
                ></message-builder-snippets>
            </template>
            <vx-button
                v-if="activeThread?.status === 'closed'"
                color="muted"
                size="lg"
                block
                @click="changeThreadState('open')"
                :disabled="actionInProgress"
                :loading="changingThreadStatus"
            >
                Re-open <font-awesome-icon widthAuto :icon="faFolderOpen"></font-awesome-icon>
            </vx-button>
        </div>
    </div>
</template>
<script setup>
import { faBan, faFolderOpen, faCircleMinus, faPerson } from '@fortawesome/pro-solid-svg-icons';
import ConversationInput from './ConversationInput.vue';
import MessageBuilderSnippets from '~/components/general/message-builder/modals/MessageBuilderSnippets.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxSwal } from '@voxie/frontend-components';
import { computed, inject, ref } from 'vue';
import { useConversation } from '~/composables/useConversation';
import { catchRequestError } from '~/utils/request';
import { actionsClient } from '~/services';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';

const FEATURES = inject('FEATURES');

const { agents } = useMessageHub();
const { contactNotFound } = useContactDetails();
const {
    activeThread,
    changingThreadStatus,
    actionInProgress,
    markAsRead,
    claimThread,
    changeActiveThreadStatus,
    sendingMessage,
    deliverMessage,
} = useConversation();

const changeThreadState = async (state, throwError) => {
    try {
        if (state === 'open') {
            await changeActiveThreadStatus(activeThread.value, state);
            await claimThread(activeThread.value);
            await markAsRead(activeThread.value);
        } else {
            await claimThread(activeThread.value);
            await markAsRead(activeThread.value);
            await changeActiveThreadStatus(activeThread.value, state);
        }
    } catch (e) {
        if (throwError) {
            throw e;
        }

        VxSwal.fire({
            title: 'Whoops!',
            text: catchRequestError(e),
            icon: 'error',
        });
    }
};

const showCloseWithSnippetsModal = ref(false);
const closeWithSnippet = async ({ body, media_url, media_content_type }) => {
    try {
        if (sendingMessage.value) {
            return;
        }

        await deliverMessage({ body, media_url, media_content_type });
        await changeThreadState('closed', true);
        actionsClient.send('snippet_used');
    } catch (e) {
        VxSwal.fire({
            title: 'Whoops!',
            text: catchRequestError(e),
            icon: 'error',
        });
    }
};

const agentName = computed(() => {
    if (!activeThread.value?.assignee_id) {
        return;
    }

    if (activeThread.value.assignee_id === Spark.state.user.id) {
        return 'You';
    }

    const agent = agents.value.find((a) => a.id === activeThread.value.assignee_id);

    return agent?.name || 'Unknown';
});
</script>
